import type { League } from '@h2h/shared';
import { NationalFlagEmblem } from '../../components/ui/NationalFlagEmblems';
import { Lock, Plus } from '../../components/ui/icons';
import { useJoinLeague, useCurrentRound, useMyEntry } from '../../hooks/useQueries';
import { cyclingLeagueMetaLine } from '../../lib/cyclingSchedule';
import { cn } from '../../lib/cn';
import { countdownTo } from '../../lib/format';
import { JoinedPill } from './Badges';
import { isRoundInPlay } from './helpers';

export function CyclingLeagueRow({
  league,
  joined,
  uid,
  onOpen
}: {
  league: League;
  joined: boolean;
  uid: string | null;
  onOpen: () => void;
}) {
  const join = useJoinLeague(uid);
  const { data: round } = useCurrentRound(league.leagueId, league.currentRoundId);
  const { data: entry } = useMyEntry(league.leagueId, league.currentRoundId, uid);

  const inPlay = isRoundInPlay(round?.status);
  const open = round?.status === 'OPEN' || round?.status === 'LOCKING';
  const needsPicks = open && !entry;
  // Group + the race date window (always available from the league doc).
  const metaLine = cyclingLeagueMetaLine(league);
  const handle = () => {
    if (!inPlay && !joined) return;
    if (!joined) {
      if (!join.isPending) join.mutate(league.leagueId, { onSuccess: onOpen });
      return;
    }
    onOpen();
  };

  const statusLine = (() => {
    if (inPlay && needsPicks && round) {
      return `${metaLine} · ${round.label} · locks ${countdownTo(round.firstKickoffAt)}`;
    }
    if (inPlay && round) {
      return `${metaLine} · ${round.label}`;
    }
    return metaLine;
  })();

  return (
    <button
      type="button"
      onClick={handle}
      disabled={join.isPending || (!inPlay && !joined)}
      className={cn(
        'flex w-full items-center gap-3 rounded-[16px] border p-[11px_12px] text-left disabled:opacity-70',
        inPlay
          ? 'tap border-[1.5px] border-accent/50 bg-[#F0FBF5]'
          : 'border-black/[0.07] bg-white',
        !inPlay && !joined && 'cursor-default'
      )}
    >
      <NationalFlagEmblem
        leagueId={league.leagueId}
        country={league.country}
        slug={league.cycling?.slug}
        size="sm"
      />
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-bold tracking-snug text-ink">{league.name}</div>
        <div
          className={cn(
            'mt-0.5 text-[11.5px] font-semibold',
            inPlay ? 'font-bold text-accent-ink' : 'text-ink2'
          )}
        >
          {inPlay && needsPicks ? (
            <span className="inline-flex items-center gap-[5px]">
              <span className="h-[5px] w-[5px] animate-h2h-pulse rounded-full bg-accent" />
              {statusLine}
            </span>
          ) : (
            statusLine
          )}
        </div>
      </div>
      {joined ? (
        <JoinedPill />
      ) : inPlay ? (
        <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-black/[0.06] bg-[#F2F2F6]">
          <Plus width={13} height={13} strokeWidth={2.6} className="text-ink2" />
        </div>
      ) : (
        <Lock width={14} height={14} strokeWidth={2.4} className="shrink-0 text-ink2" />
      )}
    </button>
  );
}
